import { CalendarDays, Clock, FileText, HardHat, House, Menu, ShieldCheck, Users, type LucideIcon } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useRole } from '../lib/compte'
import { totauxImpayes } from '../lib/impayes'
import { ENTREES } from './entrees'
import { useImpayes } from './impayes'

type Onglet = { to: string; libelle: string; Icone: LucideIcon }

const ARTISAN: Onglet[] = [
  { to: '/', libelle: 'Accueil', Icone: House },
  { to: '/devis', libelle: 'Devis', Icone: FileText },
  { to: '/clients', libelle: 'Clients', Icone: Users },
  { to: '/chantiers', libelle: 'Chantiers', Icone: HardHat },
  { to: '/plus', libelle: 'Plus', Icone: Menu },
]

// L'ouvrier n'a que ses chantiers, son planning, ses heures et son compte
const OUVRIER: Onglet[] = [
  { to: '/chantiers', libelle: 'Chantiers', Icone: HardHat },
  { to: '/planning', libelle: 'Planning', Icone: CalendarDays },
  { to: '/heures', libelle: 'Heures', Icone: Clock },
  { to: '/compte', libelle: 'Compte', Icone: ShieldCheck },
]

const concerne = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`))

/** Barre d'onglets du bas. Pour l'artisan, « Plus » reçoit tous les écrans sans onglet (Impayés, Réglages…). */
export function BarreOnglets() {
  const role = useRole()
  const { pathname } = useLocation()
  const onglets = role === 'ouvrier' ? OUVRIER : ARTISAN
  const actif = onglets.find((o) => o.to !== '/plus' && concerne(pathname, o.to))?.to ?? (role === 'ouvrier' ? undefined : '/plus')

  return (
    <nav
      aria-label="Navigation principale"
      className="sticky bottom-0 z-20 flex h-(--h-onglets) lg:hidden border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom,0px)]"
    >
      {onglets.map(({ to, libelle, Icone }) => {
        const estActif = to === actif
        return (
          <Link
            key={to}
            to={to}
            aria-current={estActif ? 'page' : undefined}
            className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium ${estActif ? 'font-bold text-nuit' : 'text-slate-600'}`}
          >
            <Icone aria-hidden className="size-6" />
            {libelle}
            {estActif && <span aria-hidden className="absolute bottom-1 h-1 w-10 rounded-full bg-primaire" />}
          </Link>
        )
      })}
    </nav>
  )
}

/** Menu latéral des grands écrans : il remplace la barre d'onglets et liste tous les écrans, pas seulement cinq. */
export function BarreLaterale() {
  const role = useRole()
  const { pathname } = useLocation()
  const impayes = useImpayes()
  const enRetard = impayes ? totauxImpayes(impayes).nombreRetard : 0
  const entrees =
    role === 'ouvrier'
      ? OUVRIER.map((o) => ({ to: o.to, titre: o.libelle, Icone: o.Icone, couleur: 'bg-nuit' }))
      : [{ to: '/', titre: 'Accueil', Icone: House, couleur: 'bg-nuit' }, ...ENTREES.filter((e) => !e.proprietaire || role === 'proprietaire')]
  return (
    <nav aria-label="Menu" className="hidden w-60 shrink-0 flex-col gap-1 overflow-y-auto border-r border-slate-200 bg-white p-3 lg:flex">
      {entrees.map(({ to, titre, Icone, couleur }) => {
        const actif = concerne(pathname, to)
        return (
          <Link
            key={to}
            to={to}
            aria-current={actif ? 'page' : undefined}
            className={`flex items-center gap-3 rounded-xl border-l-4 px-2 py-1.5 text-sm ${actif ? 'border-primaire bg-slate-100 font-bold' : 'border-transparent font-medium hover:bg-slate-50'}`}
          >
            <span className={`flex size-8 shrink-0 items-center justify-center rounded-lg text-white ${couleur}`}>
              <Icone aria-hidden className="size-4" />
            </span>
            {titre}
            {to === '/impayes' && enRetard > 0 && (
              <span role="img" aria-label={`${enRetard} en retard`} className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-bold text-white">
                {enRetard}
              </span>
            )}
          </Link>
        )
      })}
    </nav>
  )
}
